// In-call screen (docs/06 §2, §5): huge touch targets on a bottom bar (mute /
// speaker / hang up), call timer, connection-quality bar, and the degraded-
// network behaviors from the §5 table — quality drops change only the bar,
// ICE reconnects show a banner + soft tone while controls stay live and the
// timer keeps counting, and a failed reconnect ends with an explicit
// "Connection lost" plus one-tap Call back.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Friend } from '@merkury/protocol';
import { useT, type MessageKey, type TFunc } from '../../i18n/index.js';
import { candidateSeed } from '../../avatar/mercury.js';
import { MercuryAvatar } from '../../avatar/MercuryAvatar.js';
import type { CallEngineLike } from './engineTypes.js';
import { useScreens } from '../ScreensContext.js';
import { useIsDesktop } from '../ui/AppShell.js';
import { useStore } from '../ui/useStore.js';
import { createReconnectTone, createRingbackTone, type TonePlayer } from '@merkury/brain/call/reconnectTone.js';
import type { ActiveCallState, CallEndCause, CallEngineState, QualityBar } from '@merkury/brain/call/types.js';
import { formatSafetyNumber, type SafetyNumber } from '@merkury/brain/call/crypto/safetyNumber.js';
import { microphonePermissionState } from '@merkury/brain/call/media/micPermission.js';
import { isCapacitorNative } from '../../shell/capacitor/platform.js';
import { formatElapsed as formatDuration } from '../ui/callFormat.js';
import { displayNameFor } from '../ui/peerName.js';
import { initialOf } from '../ui/PeerAvatar.js';

// A dead-end route: enter `/call` with no engine call and no last-ended
// summary (stale route, back-navigation, or a call that already fully
// cleared) and there is nothing to show. Bounce home — but not instantly: a
// cold-start accept deep link renders this screen a beat before the engine's
// `call` state materializes, so give it a short settling window first.
const DEAD_END_GRACE_MS = 2_000;

const END_MESSAGES: Record<CallEndCause, MessageKey> = {
  hangup: 'call.end.hangup',
  declined: 'call.end.declined',
  busy: 'call.end.busy',
  no_answer: 'call.end.no_answer',
  missed: 'call.end.missed',
  answered_elsewhere: 'call.end.answered_elsewhere',
  connection_lost: 'call.end.connection_lost',
  failed: 'call.end.failed',
  mic_denied: 'call.end.mic_denied',
  security: 'call.end.security',
};

const BAR_CLASSES: Record<QualityBar, string> = {
  green: 'bg-ok',
  yellow: 'bg-warn',
  red: 'bg-bad',
};

const PHASE_LABELS: Record<string, MessageKey> = {
  dialing: 'call.phase.dialing',
  ringing_remote: 'call.phase.ringing_remote',
  connecting: 'call.phase.connecting',
  reconnecting: 'call.phase.reconnecting',
};

export function InCallScreen({
  tone,
  ringback,
}: {
  tone?: TonePlayer;
  ringback?: TonePlayer;
}): ReactNode {
  const t = useT();
  const { engine, api } = useScreens();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const engineState = useStore(engine.state);
  const { call, lastEnded, keyChange } = engineState;

  const [friends, setFriends] = useState<Friend[]>([]);
  useEffect(() => {
    let cancelled = false;
    void api
      .listFriends()
      .then((res) => {
        if (!cancelled) setFriends(res.friends);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api]);

  const peerId = call?.peerUserId ?? lastEnded?.peerUserId;
  const peerName = peerId ? displayNameFor(peerId, friends) : '';
  // Desktop pane shows the peer's mercury disc; resolve the seed exactly as the
  // ring surface does (candidateSeed(userId, avatarSeed) when the friend is
  // known), else a neutral disc. MercuryAvatar is the only draw path.
  const peerFriend = peerId ? friends.find((f) => f.userId === peerId) : undefined;
  const peerAvatarSeed =
    peerId && peerFriend?.avatarSeed !== undefined
      ? candidateSeed(peerId, peerFriend.avatarSeed)
      : undefined;

  // Dead-end guard (see DEAD_END_GRACE_MS above): arm a timer whenever there
  // is neither an active call nor a last-ended summary, and disarm it the
  // instant either shows up. `graceExpired` resets on each fresh arming so a
  // call that ends and clears again later gets its own full window.
  const [graceExpired, setGraceExpired] = useState(false);
  useEffect(() => {
    if (call || lastEnded) return undefined;
    setGraceExpired(false);
    const id = setTimeout(() => setGraceExpired(true), DEAD_END_GRACE_MS);
    return () => clearTimeout(id);
  }, [call, lastEnded]);

  useEffect(() => {
    if (!call && !lastEnded && graceExpired) {
      void navigate('/', { replace: true });
    }
  }, [call, lastEnded, graceExpired, navigate]);

  // Mic-denied end: read the Permissions API for MESSAGING only (never to
  // gate the retry — "Call back" below always re-attempts getUserMedia
  // regardless of this). A 'denied' read means Chrome will keep auto-
  // rejecting without ever showing its prompt again, so point at settings
  // instead of leaving the user in a silent retry loop. Native already gets
  // settings guidance from the base message (its permission model has no
  // separate site-settings surface), so this only adds to the web case.
  const micEndedKey = lastEnded?.cause === 'mic_denied' ? lastEnded.callId : null;
  const [micBlocked, setMicBlocked] = useState(false);
  useEffect(() => {
    setMicBlocked(false);
    if (!micEndedKey || isCapacitorNative()) return undefined;
    let cancelled = false;
    void microphonePermissionState().then((state) => {
      if (!cancelled) setMicBlocked(state === 'denied');
    });
    return () => {
      cancelled = true;
    };
  }, [micEndedKey]);

  // Timer: ticks while connected; keeps counting through `reconnecting`
  // (docs/06 §5 — "timer keeps counting").
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!call?.connectedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [call?.connectedAt]);

  // Soft repeating tone while reconnecting (docs/06 §5 table).
  const toneRef = useRef<TonePlayer | null>(null);
  toneRef.current ??= tone ?? createReconnectTone();
  const reconnecting = call?.phase === 'reconnecting';
  useEffect(() => {
    const player = toneRef.current!;
    if (reconnecting) player.start();
    else player.stop();
    return () => player.stop();
  }, [reconnecting]);

  // Caller-side ringback while the remote end rings (both platforms — nothing
  // native plays an outgoing tone).
  const ringbackRef = useRef<TonePlayer | null>(null);
  ringbackRef.current ??= ringback ?? createRingbackTone();
  const outgoingRinging =
    call?.direction === 'outgoing' && (call.phase === 'dialing' || call.phase === 'ringing_remote');
  useEffect(() => {
    const player = ringbackRef.current!;
    if (outgoingRinging) player.start();
    else player.stop();
    return () => player.stop();
  }, [outgoingRinging]);

  const elapsed = useMemo(
    () => (call?.connectedAt ? formatDuration(now - call.connectedAt) : null),
    [call?.connectedAt, now],
  );

  // Safety number (docs/03 §4.2): once the call is connected, derive it from
  // the pinned keys and show it under the timer so both sides can read it
  // aloud. Async and may resolve null (no pinned key yet) — render nothing
  // then. Kept in state across a `reconnecting` dip so it doesn't flicker.
  const connected = call?.phase === 'in_call';
  const safetyPeerId = call?.peerUserId;
  const safetyCallId = call?.callId;
  const [safetyNumber, setSafetyNumber] = useState<SafetyNumber | null>(null);
  // Reset per CALL, not per phase: the number must survive a `reconnecting`
  // dip without flicker, but a NEW call starting in this still-mounted screen
  // must not inherit the previous call's number — it rendered under
  // "Connecting…" before anything was verified (field 2026-07-19).
  useEffect(() => {
    setSafetyNumber(null);
  }, [safetyCallId]);
  useEffect(() => {
    if (!connected || !safetyPeerId) return undefined;
    let cancelled = false;
    void engine
      .safetyNumberWith(safetyPeerId)
      .then((sn) => {
        if (!cancelled) setSafetyNumber(sn);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [connected, safetyPeerId, engine]);

  // Key-change interlock (docs/06 §3): block the call UI until decided.
  if (keyChange) {
    return (
      <main className="mx-auto flex min-h-full w-full max-w-app flex-col justify-center gap-6 p-6">
        <div role="alertdialog" aria-label={t('call.keyChanged')} className="rounded-xl bg-surface p-6">
          <h1 className="text-xl font-semibold text-warn">{t('call.keyChanged')}</h1>
          <p className="mt-2 text-sm text-ink">{t('call.keyChangedBody')}</p>
          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              onClick={() => void engine.confirmKeyChange()}
              className="min-h-touch rounded-xl bg-accent px-4 font-medium text-ink active:bg-accent-press"
            >
              {t('call.trustNewKey')}
            </button>
            <button
              type="button"
              onClick={() => {
                engine.dismissKeyChange();
                void navigate('/');
              }}
              className="min-h-touch rounded-xl bg-raised px-4 text-ink"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </main>
    );
  }

  // No active call: show the last-ended summary (with the one-tap call back
  // for connection loss) or bounce home.
  if (!call) {
    if (!lastEnded) {
      return (
        <main className="mx-auto flex min-h-full w-full max-w-app flex-col items-center justify-center gap-6 p-6">
          <p className="text-sm text-muted">{t('call.noActive')}</p>
          <button
            type="button"
            onClick={() => void navigate('/')}
            className="min-h-touch rounded-xl bg-raised px-6 text-ink"
          >
            {t('common.back')}
          </button>
        </main>
      );
    }
    return (
      <main className="mx-auto flex min-h-full w-full max-w-app flex-col items-center justify-center gap-6 p-6 text-center">
        <h1 className="text-2xl font-semibold text-ink">{peerName}</h1>
        <p role="status" className="text-lg text-muted">
          {t(END_MESSAGES[lastEnded.cause])}
        </p>
        {micBlocked && (
          <p role="status" className="text-sm text-warn">
            {t('call.end.micBlocked')}
          </p>
        )}
        <div className="flex w-full flex-col gap-3">
          <button
            type="button"
            onClick={async () => {
              // Re-runs the exact same request path as any first attempt
              // (engine.dial → getUserMedia): Chrome re-prompts on its own
              // unless the site is permanently blocked (see the hint above).
              await engine.dial(lastEnded.peerUserId);
            }}
            className="min-h-touch rounded-xl bg-accent px-6 font-medium text-ink active:bg-accent-press"
          >
            {t('call.callBack')}
          </button>
          <button
            type="button"
            onClick={() => void navigate('/')}
            className="min-h-touch rounded-xl bg-raised px-6 text-ink"
          >
            {t('common.back')}
          </button>
        </div>
      </main>
    );
  }

  const phaseLabelKey = call.phase in PHASE_LABELS ? PHASE_LABELS[call.phase] : null;

  // Which button/handler for each control lives here once; the two layouts
  // (compact bottom bar, desktop pane row) differ only in chrome. Handlers and
  // aria-labels are identical across both — the engine wiring is untouched.
  const controls = controlSpecs(call, engineState, engine, t);

  const status =
    elapsed !== null ? (
      <p aria-label={t('call.duration')} className="font-mono text-xl text-ink">
        {elapsed}
      </p>
    ) : (
      phaseLabelKey && (
        <p role="status" className="text-lg text-muted">
          {t(phaseLabelKey)}
        </p>
      )
    );
  const safety = safetyNumber && (
    <div
      role="group"
      aria-label={t('call.safetyNumber')}
      className="mt-1 flex flex-col items-center gap-0.5"
    >
      <span aria-hidden="true" className="text-xs text-muted">
        {t('call.safetyNumber')}
      </span>
      {/* Digits only (no emoji), grouped like the Android SafetyNumberPanel; the
          aria-label spells each digit so a screen reader doesn't read the group
          as one large number. */}
      <span
        className="font-mono text-xs text-muted"
        aria-label={safetyNumber.digits.split('').join(' ')}
      >
        {formatSafetyNumber(safetyNumber)}
      </span>
    </div>
  );
  const banner = reconnecting && (
    <div
      role="status"
      data-testid="reconnecting-banner"
      className="mt-4 rounded-xl bg-surface px-4 py-3 text-warn"
    >
      {t('call.phase.reconnecting')}
    </div>
  );

  // Desktop: the call surface centered in the pane — peer mercury disc, name,
  // timer, quality bar, and the round labelled control row (prototype look).
  if (isDesktop) {
    return (
      <main
        data-testid="incall-desktop"
        className="flex min-h-full flex-1 flex-col items-center justify-center gap-3 p-8 text-center"
      >
        {peerAvatarSeed ? (
          <MercuryAvatar seed={peerAvatarSeed} size={110} className="rounded-full" />
        ) : (
          <div
            aria-hidden="true"
            className="flex h-[110px] w-[110px] items-center justify-center rounded-full bg-raised text-4xl font-semibold text-muted"
          >
            {peerName ? initialOf(peerName) : ''}
          </div>
        )}
        <h1 className="mt-2 text-2xl font-semibold text-ink">{peerName}</h1>
        {status}
        {safety}
        {call.viaRelay && <p className="text-xs text-muted">{t('call.viaRelay')}</p>}
        <QualityMeter bar={call.bar} label={t('call.connectionQuality')} />
        {banner}
        <ControlRow variant="desktop" controls={controls} label={t('call.controls')} />
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col p-6">
      <section className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
        <h1 className="text-3xl font-semibold text-ink">{peerName}</h1>
        {status}
        {safety}
        {call.viaRelay && <p className="text-xs text-muted">{t('call.viaRelay')}</p>}

        {/* Connection quality bar (docs/06 §3): green/yellow/red, no modal. */}
        <QualityMeter bar={call.bar} label={t('call.connectionQuality')} />

        {banner}
      </section>

      {/* Thumb-reachable bottom bar; controls stay active in every phase. */}
      <ControlRow variant="compact" controls={controls} label={t('call.controls')} />
    </main>
  );
}

// --- Shared call-surface pieces ---------------------------------------------

/** Connection-quality meter (docs/06 §3): green/yellow/red, no modal. Identical
 *  markup in both layouts so the a11y contract and tests never diverge. */
function QualityMeter({ bar, label }: { bar: QualityBar; label: string }): ReactNode {
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuenow={bar === 'green' ? 2 : bar === 'yellow' ? 1 : 0}
      aria-valuemin={0}
      aria-valuemax={2}
      aria-valuetext={bar}
      data-bar={bar}
      className="mt-4 h-2 w-40 overflow-hidden rounded-full bg-raised"
    >
      <div className={`h-full ${BAR_CLASSES[bar]}`} style={{ width: '100%' }} />
    </div>
  );
}

/** A single control: which handler/label/glyph/state — computed once, rendered
 *  by both layouts. `onClass` is the compact "on" fill; desktop uses the
 *  prototype's ink-on active pad instead. */
interface ControlSpec {
  key: string;
  ariaLabel: string;
  ariaPressed?: boolean;
  testId?: string;
  dataRoute?: string;
  onClick: () => void;
  icon: ReactNode;
  on: boolean;
  onClass: string;
  danger?: boolean;
  label: string;
}

function controlSpecs(
  call: ActiveCallState,
  engineState: CallEngineState,
  engine: CallEngineLike,
  t: TFunc,
): ControlSpec[] {
  const mute: ControlSpec = {
    key: 'mute',
    ariaLabel: call.muted ? t('call.unmute') : t('call.mute'),
    ariaPressed: call.muted,
    onClick: () => engine.setMuted(!call.muted),
    icon: <MicIcon off={call.muted} />,
    on: call.muted,
    onClass: 'bg-warn text-bg',
    label: t('call.muteShort'),
  };

  const audio: ControlSpec = engineState.audio
    ? // Native (Android): route cycle — earpiece → speaker → Bluetooth (when a
      // BT device is present) → earpiece. The shown state is what the audio
      // ACTUALLY plays on (native feedback), so an automatic switch (headphones
      // connect mid-call) is reflected immediately.
      {
        key: 'audio',
        ariaLabel: t(ROUTE_LABEL[engineState.audio.route]),
        testId: 'route-button',
        dataRoute: engineState.audio.route,
        onClick: () =>
          engine.requestAudioRoute(
            nextRoute(
              engineState.audio?.route ?? 'EARPIECE',
              engineState.audio?.bluetoothAvailable ?? false,
            ),
          ),
        icon:
          engineState.audio.route === 'BLUETOOTH' ? (
            <BluetoothIcon />
          ) : (
            <SpeakerIcon on={engineState.audio.route === 'SPEAKER'} />
          ),
        on: engineState.audio.route !== 'EARPIECE',
        onClass: 'bg-accent text-ink',
        label: t('call.audioShort'),
      }
    : {
        key: 'audio',
        ariaLabel: call.speaker ? t('call.speakerOff') : t('call.speakerOn'),
        ariaPressed: call.speaker,
        onClick: () => engine.setSpeaker(!call.speaker),
        icon: <SpeakerIcon on={call.speaker} />,
        on: call.speaker,
        onClass: 'bg-accent text-ink',
        label: t('call.audioShort'),
      };

  const end: ControlSpec = {
    key: 'end',
    ariaLabel: t('call.endCall'),
    onClick: () => engine.hangup(),
    icon: <HangUpIcon />,
    on: false,
    onClass: '',
    danger: true,
    label: t('call.endShort'),
  };

  return [mute, audio, end];
}

function ControlRow({
  variant,
  controls,
  label,
}: {
  variant: 'compact' | 'desktop';
  controls: ControlSpec[];
  label: string;
}): ReactNode {
  if (variant === 'desktop') {
    return (
      <section
        aria-label={label}
        className="mt-4 flex items-center justify-center gap-5"
      >
        {controls.map((c) => (
          <button
            key={c.key}
            type="button"
            aria-label={c.ariaLabel}
            {...(c.ariaPressed !== undefined ? { 'aria-pressed': c.ariaPressed } : {})}
            {...(c.testId ? { 'data-testid': c.testId } : {})}
            {...(c.dataRoute ? { 'data-route': c.dataRoute } : {})}
            onClick={c.onClick}
            className="flex flex-col items-center gap-1.5 text-xs text-muted"
          >
            <span
              aria-hidden="true"
              className={`flex h-[52px] w-[52px] items-center justify-center rounded-full border ${
                c.danger
                  ? 'border-danger bg-danger text-ink'
                  : c.on
                    ? 'border-edge bg-ink text-bg'
                    : 'border-edge bg-surface text-ink'
              }`}
            >
              {c.icon}
            </span>
            {/* Decorative echo of the aria-label — the button's accessible name
                is the full aria-label (e.g. "Speaker on"), so this short label
                stays hidden to avoid a label-in-name mismatch. */}
            <span aria-hidden="true">{c.label}</span>
          </button>
        ))}
      </section>
    );
  }

  return (
    <section aria-label={label} className="flex items-center justify-around pb-4">
      {controls.map((c) => (
        <button
          key={c.key}
          type="button"
          aria-label={c.ariaLabel}
          {...(c.ariaPressed !== undefined ? { 'aria-pressed': c.ariaPressed } : {})}
          {...(c.testId ? { 'data-testid': c.testId } : {})}
          {...(c.dataRoute ? { 'data-route': c.dataRoute } : {})}
          onClick={c.onClick}
          className={`flex h-16 w-16 items-center justify-center rounded-full text-2xl ${
            c.danger ? 'bg-danger text-ink' : c.on ? c.onClass : 'bg-raised text-ink'
          }`}
        >
          {c.icon}
        </button>
      ))}
    </section>
  );
}

// --- Audio route cycle (native only) ----------------------------------------

const ROUTE_LABEL = {
  EARPIECE: 'call.routeEarpiece',
  SPEAKER: 'call.routeSpeaker',
  BLUETOOTH: 'call.routeBluetooth',
  WIRED_HEADSET: 'call.routeWired',
} as const;

/** Earpiece → speaker → Bluetooth (when present) → earpiece. A wired headset
 *  behaves like earpiece for cycling purposes (next stop: speaker). */
function nextRoute(
  current: 'EARPIECE' | 'SPEAKER' | 'BLUETOOTH' | 'WIRED_HEADSET',
  bluetoothAvailable: boolean,
): 'EARPIECE' | 'SPEAKER' | 'BLUETOOTH' {
  if (current === 'SPEAKER') return bluetoothAvailable ? 'BLUETOOTH' : 'EARPIECE';
  if (current === 'BLUETOOTH') return 'EARPIECE';
  return 'SPEAKER';
}

// --- Control icons (inline SVG; emoji render inconsistently across OEM fonts
// and the crossed-out-speaker mute glyph read as a speaker control) ----------

function BluetoothIcon(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-7 w-7"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 7l10 10-5 4V3l5 4L7 17" />
    </svg>
  );
}

function MicIcon({ off }: { off: boolean }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-7 w-7"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
      {off && <path d="M4 3l17 17" className="text-current" />}
    </svg>
  );
}

function SpeakerIcon({ on }: { on: boolean }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-7 w-7"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 5 6.5 9H3v6h3.5L11 19z" />
      <path d="M15.5 9.5a5 5 0 0 1 0 5" />
      {on && <path d="M18.5 7a9 9 0 0 1 0 10" />}
    </svg>
  );
}

function HangUpIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor" aria-hidden="true">
      <path d="M12 10.5c-3.9 0-7.4 1.4-9.7 3.7a1 1 0 0 0 0 1.4l2 2a1 1 0 0 0 1.4 0l1.9-1.6c.3-.2.4-.6.4-.9v-2a12.6 12.6 0 0 1 8 0v2c0 .3.1.7.4.9l1.9 1.6a1 1 0 0 0 1.4 0l2-2a1 1 0 0 0 0-1.4c-2.3-2.3-5.8-3.7-9.7-3.7z" />
    </svg>
  );
}
